import { apiClient } from './base';
import { store } from '../../store';

// Types
export interface DoctorOffice {
  id: string;
  office_id: string;
  office_name: string;
  office_number: string;
  department?: string;
  floor?: string;
  building?: string;
  institution_id: string;
  status: 'active' | 'inactive' | 'maintenance' | 'temporary_closed';
  assigned_doctors: string[];
  primary_doctor?: string;
  assigned_machines: string[];
  primary_machine?: string;
  operating_hours?: any;
  max_daily_appointments?: number;
  appointment_duration?: number;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface DoctorOfficeCreateRequest {
  office_id: string;
  office_name: string;
  office_number: string;
  department?: string;
  floor?: string;
  building?: string;
  institution_id?: string; // Optional - auto-assigned if not provided
  description?: string;
  max_daily_appointments?: number;
  appointment_duration?: number;
}

export interface DoctorOfficeUpdateRequest {
  office_name?: string;
  office_number?: string;
  department?: string;
  floor?: string;
  building?: string;
  status?: 'active' | 'inactive' | 'maintenance' | 'temporary_closed';
  description?: string;
  max_daily_appointments?: number;
  appointment_duration?: number;
}

export interface OfficeMachineAssignment {
  id: string;
  office_id: string;
  machine_id: string;
  assignment_type: 'primary' | 'secondary' | 'backup';
  default_ae_title?: string;
  priority_order: number;
  is_active: boolean;
  created_at: string;
}

export interface OfficeMachineAssignmentRequest {
  office_id: string;
  machine_id: string;
  assignment_type: 'primary' | 'secondary' | 'backup';
  default_ae_title?: string;
  priority_order?: number;
}

export interface OfficeMachinePair {
  office_id: string;
  office_name?: string; // Legacy support
  machine_id: string;
  machine_name?: string; // Legacy support
  ae_title?: string; // Legacy support
  default_ae_title?: string; // Legacy support
  office_details?: {
    office_name: string;
    [key: string]: any;
  };
  machine_details?: {
    machine_name: string;
    ae_title?: string;
    [key: string]: any;
  };
}

// Doctor Offices API
export const doctorOfficesAPI = {
  // Get all doctor offices
  async getOffices(): Promise<DoctorOffice[]> {
    // Obtener institution_code del usuario autenticado
    const user = store.getState().auth.user;
    // institution_code debe estar en user.institution_code
    let institution_code = '';
    if (user && user.institution_code) {
      institution_code = user.institution_code;
    }
    const response = await apiClient.get('/api/v1/doctor-offices', {
      params: institution_code ? { institution_code } : {}
    });
    const body = response.data;
    if (Array.isArray(body?.offices)) {
      return body.offices;
    }
    if (body?.success && Array.isArray(body.offices)) {
      return body.offices;
    }
    if (Array.isArray(body)) {
      return body;
    }
    return [];
  },

  // Get a specific doctor office
  async getOffice(officeId: string): Promise<DoctorOffice> {
    const response = await apiClient.get(`/api/v1/doctor-offices/${officeId}`);
    return response.data;
  },

  // Create a new doctor office
  async createOffice(office: DoctorOfficeCreateRequest): Promise<DoctorOffice> {
    const response = await apiClient.post('/api/v1/doctor-offices', office);
    // Handle new structured response format
    if (response.data && response.data.data) {
      return response.data.data;
    }
    // Fallback for legacy format
    return response.data;
  },

  // Update an existing doctor office
  async updateOffice(officeId: string, office: DoctorOfficeUpdateRequest): Promise<DoctorOffice> {
    const response = await apiClient.put(`/api/v1/doctor-offices/${officeId}`, office);
    return response.data;
  },

  // Delete a doctor office
  async deleteOffice(officeId: string, forceCascade: boolean = false): Promise<void> {
    const params = forceCascade ? { force_cascade: 'true' } : {};
    await apiClient.delete(`/api/v1/doctor-offices/${officeId}`, { params });
  },

  // Office-Machine Assignment endpoints
  
  // Get all assignments for an office
  async getOfficeAssignments(officeId: string): Promise<OfficeMachineAssignment[]> {
    const response = await apiClient.get(`/api/v1/doctor-offices/${officeId}/assignments`);
    return response.data;
  },

  // Get all assignments (all offices)
  async getAllAssignments(): Promise<OfficeMachineAssignment[]> {
    try {
      // Obtener institution_code del usuario autenticado
      const user = store.getState().auth.user;
      let institution_code = '';
      if (user && user.institution_code) {
        institution_code = user.institution_code;
      }
      const response = await apiClient.get('/api/v1/doctor-offices/assignments', {
        params: institution_code ? { institution_code } : {}
      });
      console.log('🔍 [API DEBUG] getAllAssignments response.data:', response.data);
      
      // Handle new structured response format {success: true, data: Array, count: number}
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        console.log('🔍 [API DEBUG] Extracting response.data.data:', response.data.data);
        return response.data.data;
      }
      
      // Fallback for legacy array format
      if (Array.isArray(response.data)) {
        console.log('🔍 [API DEBUG] Using legacy array format:', response.data);
        return response.data;
      }
      
      console.log('🔍 [API DEBUG] No valid data found, returning empty array');
      return [];
    } catch (error: any) {
      // Log error but don't throw for 404 errors that might be handled at component level
      if (error.response?.status === 404) {
        console.warn('⚠️ Assignments API endpoint not found - may be handled at component level');
      } else if (error.response?.status === 400) {
        console.warn('⚠️ Assignments API returned 400 - may be handled at component level:', error.message);
      } else {
        console.error('❌ Error fetching assignments:', error);
      }
      throw error;
    }
  },

  // Assign a machine to an office
  async assignMachine(assignment: OfficeMachineAssignmentRequest): Promise<OfficeMachineAssignment> {
    const response = await apiClient.post(`/api/v1/doctor-offices/${assignment.office_id}/machines`, {
      office_id: assignment.office_id, // Include office_id as required by backend model
      machine_id: assignment.machine_id,
      assignment_type: assignment.assignment_type,
      default_ae_title: assignment.default_ae_title,
      priority_order: assignment.priority_order
    });
    return response.data;
  },

  // Update an assignment
  async updateAssignment(
    assignmentId: string, 
    assignment: Partial<OfficeMachineAssignmentRequest>
  ): Promise<OfficeMachineAssignment> {
    const response = await apiClient.put(`/api/v1/doctor-offices/assignments/${assignmentId}`, assignment);
    return response.data;
  },

  // Remove a machine assignment
  async removeAssignment(assignment: OfficeMachineAssignment): Promise<void> {
    await apiClient.delete(`/api/v1/doctor-offices/${assignment.office_id}/machines/${assignment.machine_id}`);
  },

  // Get available office-machine pairs for worklist creation
  async getAvailablePairs(): Promise<OfficeMachinePair[]> {
    const parsePairs = (body: unknown): OfficeMachinePair[] | null => {
      if (!body || typeof body !== 'object') {
        return null;
      }
      const record = body as { data?: unknown; success?: boolean };
      if (Array.isArray(record.data)) {
        return record.data as OfficeMachinePair[];
      }
      if (Array.isArray(body)) {
        return body as OfficeMachinePair[];
      }
      return null;
    };

    try {
      const response = await apiClient.get('/api/v1/doctor-offices/available-pairs');
      const pairs = parsePairs(response.data);
      if (pairs) {
        return pairs;
      }
      console.warn('Unexpected office-machine pairs response structure:', response.data);
    } catch (error) {
      console.warn('available-pairs failed, falling back to assignments:', error);
    }

    // Fallback: assignments endpoint returns the same enriched shape (active + inactive).
    const assignmentsResponse = await apiClient.get('/api/v1/doctor-offices/assignments');
    const assignments = parsePairs(assignmentsResponse.data) ?? [];
    return assignments.filter((a) => a.is_active !== false);
  },

  // Validate office-machine pairing
  async validatePairing(officeId: string, machineId: string): Promise<{ valid: boolean; message?: string }> {
    const response = await apiClient.get(`/api/v1/doctor-offices/validate-pairing`, {
      params: { office_id: officeId, machine_id: machineId }
    });
    return response.data;
  }
};

export default doctorOfficesAPI;
